import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useCountdown from "./useCountdown";
import { currencies } from "../../Utility/currencies";

const CRIMSON = "#a31545";
const CRIMSON_DEEP = "#7d1733";

const QuickWin = ({ title, duration, was, now, userCurrency }) => (
  <Box
    sx={{
      background: "#f4eef0",
      borderRadius: 3,
      px: 2,
      py: 2,
      textAlign: "center",
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        background: "#fff",
        color: CRIMSON,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 1,
        fontWeight: 900,
      }}
    >
      <FiberManualRecordIcon fontSize="small" />
    </Box>
    <Typography sx={{ fontWeight: 700, color: "#2b2b2b" }}>{title}</Typography>
    <Typography sx={{ fontSize: 12, color: "#8a8a8a", mb: 1 }}>
      ~ {duration}
    </Typography>
    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
      <Typography
        sx={{ fontSize: 12, color: "#b9909c", textDecoration: "line-through" }}
      >
        +{was}
      </Typography>
      <Box
        sx={{
          background: "#f6d3de",
          color: CRIMSON,
          fontWeight: 800,
          fontSize: 12,
          px: 1,
          py: 0.25,
          borderRadius: 1.5,
        }}
      >
        + {userCurrency} {now} 
      </Box>
    </Stack>
  </Box>
);

const HappyHourLive = ({ open, onClose, status, onExpire, onPlayNow }) => {
  const { t } = useTranslation();
  const { clock } = useCountdown(
    status?.secondsUntilEnd ? Number(status.secondsUntilEnd) : 0,
    onExpire
  );

  const IsLoginData = useSelector((state) => state.auth.IsLoginData);
  const userCurrency =
    currencies.find((c) => c.code === (IsLoginData?.currency || "USD"))
      ?.symbol || "$";

  if (!status) return null;

  const multiplier = status.multiplier || status.coinMultiplier || 2;
  const coinMultiplier = status.coinMultiplier || multiplier;
  const claimed = !!status.bonusDropClaimed;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 10101 }}
      PaperProps={{
        sx: { borderRadius: 4, overflow: "hidden", background: "#fff" },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 8, right: 8, color: "#fff", zIndex: 2 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Live hero */}
        <Box
          sx={{
            background: `linear-gradient(160deg, ${CRIMSON} 0%, ${CRIMSON_DEEP} 100%)`,
            color: "#fff",
            textAlign: "center",
            px: 3,
            py: 3.5,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              background: "rgba(255,255,255,0.18)",
              borderRadius: 5,
              px: 1.5,
              py: 0.4,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 1,
            }}
          >
            <FiberManualRecordIcon sx={{ fontSize: 10 }} />{" "}
            {t("happyHour.liveNowLabel")}
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 24, mt: 1 }}>
            {t("happyHour.liveTitle")}
          </Typography>
          <Typography sx={{ fontWeight: 900, fontSize: 60, lineHeight: 1.05 }}>
            {multiplier}x
          </Typography>
          <Typography sx={{ opacity: 0.9, fontSize: 14 }}>
            {multiplier === 2
              ? t("happyHour.liveAllRewardsDoubled")
              : t("happyHour.liveAllRewardsBoosted")}
          </Typography>

          <Box
            sx={{
              mt: 2,
              background: "rgba(0,0,0,0.22)",
              borderRadius: 2,
              py: 1.25,
            }}
          >
            <Typography
              sx={{ fontSize: 11, letterSpacing: 1.5, opacity: 0.85 }}
            >
              {t("happyHour.liveEndsInLabel")}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 30,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: 1,
              }}
            >
              {clock}
            </Typography>
          </Box>
        </Box>

        {/* Quick wins */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography
            sx={{ fontSize: 12, color: "#9a9a9a", fontWeight: 700, mb: 1 }}
          >
            {t("happyHour.liveQuickWinsHeading")}
          </Typography>
          <Stack spacing={1.5}>
            <QuickWin
              title={t("happyHour.liveQuickWinDailyTitle")}
              duration={t("happyHour.liveDurationMinutes", { minutes: 2 })}
              was={25}
              now={25 * coinMultiplier}
              userCurrency={userCurrency}
            />
            <QuickWin
              title={t("happyHour.liveQuickWinBonusTitle")}
              duration={t("happyHour.liveDurationMinutes", { minutes: 5 })}
              was={100}
              now={100 * coinMultiplier}
              userCurrency={userCurrency}
            />
          </Stack>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          <Button
            fullWidth
            disabled={claimed}
            onClick={onPlayNow || onClose}
            sx={{
              background: claimed
                ? "#e6e0e2"
                : `linear-gradient(160deg, ${CRIMSON} 0%, ${CRIMSON_DEEP} 100%)`,
              color: "#fff",
              fontWeight: 800,
              textTransform: "none",
              borderRadius: 2,
              py: 1.2,
              "&:hover": { opacity: 0.92 },
              "&.Mui-disabled": { color: "#9a9a9a", background: "#ececec" },
            }}
          >
            {claimed
              ? t("happyHour.liveAlreadyClaimed")
              : t("happyHour.livePlayNow")}
          </Button>
          {claimed && (
            <Typography
              sx={{ mt: 1, textAlign: "center", fontSize: 12, color: "#8a8a8a" }}
            >
              {t("happyHour.liveClaimedNote")}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HappyHourLive;
